import Link from 'next/link'
import { Breadcrumb } from '@/commons/Breadcrumb'
import Head from 'next/head'
import { PersonalTrainerHead } from '../../../components/index/service-category/personal-trainer/PersonalTrainerHead'
import { openModal } from 'service/modal-service'
import { RequestProvider } from 'context/RequestContext'
import { PersonalTrainerBooking } from '../../../components/index/service-category/personal-trainer/PersonalTrainerBooking'
import Script from 'next/script'
import { useRouter } from 'next/router'
import { useState } from 'react'
import { contentDate } from '../../../utils/article-date-time'
import { getRequestJSON } from '../../../api/index/location-api'
import { s3Static } from '../../../utils/constants'
import { motion } from 'framer-motion'
import { FaUserMd, FaClipboardCheck, FaDumbbell, FaHeartbeat, FaRulerCombined, FaRunning, FaCheckCircle } from 'react-icons/fa'

// When this page's content last actually changed. Bump it by hand when you
// edit the copy - not for layout/UX changes. Feeds dateModified and
// article:modified_time (see reviewedIso in utils/article-date-time).
const CONTENT_REVIEWED = '2026-09-21'

// This function gets called at build time on server-side.
// It may be called again, on a serverless function, if
// revalidation is enabled and a new request comes in
export async function getStaticProps() {

    const { modifiedDate, month, year } = contentDate(CONTENT_REVIEWED);

    const countryName = "Singapore"

    const allRequestCategorySettings = await getRequestJSON(countryName)

    const requestCategorySettings = allRequestCategorySettings.filter(category => category.requestType === "Fitness Trainer")

  return {
      props: {
        requestCategorySettings,
        countryName,
        modifiedDate,
        month,
        year
      },
      // Next.js will attempt to re-generate the page:
      // - When a request comes in
      // - At most once every 10 seconds
      revalidate: 259200, // In seconds
  }
}

export default function WhatToExpect(props) {
    const requestCategorySettings = props.requestCategorySettings
    const modifiedDate = props.modifiedDate
    const year = props.year

    const router = useRouter()

    const [requestForm, setRequestForm] = useState(true)

    const requestType = "Fitness Trainer"
    const [isQuickBook, setIsQuickBook] = useState(false)
    const [isCustomBook, setIsCustomBook] = useState(false)

    const openRequestForm = () => {
        window.scrollTo({ top: 0 });
        openModal(setRequestForm)
    }

    const consultationSteps = [
        {
            icon: <FaUserMd className="text-blue-500" size={24} />,
            title: "Welcome & Goal Discussion",
            description: "Your trainer introduces themselves and creates a comfortable environment. They discuss your fitness goals, timeline, and expectations to understand what you want to achieve."
        },
        {
            icon: <FaClipboardCheck className="text-green-500" size={24} />,
            title: "Lifestyle Assessment",
            description: "Detailed discussion about your daily routine, work schedule, sleep patterns, and stress levels to understand how fitness fits into your lifestyle."
        },
        {
            icon: <FaHeartbeat className="text-red-500" size={24} />,
            title: "Eating Habits Analysis",
            description: "Review of your meal frequency, food preferences, snacking habits, and current nutrition patterns to identify areas for improvement."
        },
        {
            icon: <FaCheckCircle className="text-purple-500" size={24} />,
            title: "Dietary Restrictions Review",
            description: "Identification of allergies, intolerances, cultural preferences, or religious dietary requirements that need to be considered."
        },
        {
            icon: <FaDumbbell className="text-orange-500" size={24} />,
            title: "Fitness Goals & Experience",
            description: "Clarification of specific goals (weight loss, muscle gain, endurance) and assessment of your current fitness level and exercise history."
        },
        {
            icon: <FaRulerCombined className="text-indigo-500" size={24} />,
            title: "Medical History Review",
            description: "Discussion of any medical conditions, previous injuries, surgeries, or medications that could affect your training program."
        }
    ]

    const assessmentSteps = [
        {
            icon: <FaRunning className="text-blue-500" size={24} />,
            title: "Warm-up & Mobility Screening",
            description: "Gentle warm-up exercises followed by joint mobility tests to check range of motion and identify any stiffness or limitations."
        },
        {
            icon: <FaDumbbell className="text-green-500" size={24} />,
            title: "Strength Assessment",
            description: "Bodyweight exercises like push-ups, squats, and planks to evaluate muscular strength and identify strength imbalances."
        },
        {
            icon: <FaHeartbeat className="text-red-500" size={24} />,
            title: "Cardiovascular Fitness Check",
            description: "Heart rate monitoring and light cardio exercises to assess cardiovascular endurance and resting heart rate."
        },
        {
            icon: <FaRulerCombined className="text-purple-500" size={24} />,
            title: "Body Measurements",
            description: "Height, weight, BMI calculation, and body circumference measurements to establish baseline metrics."
        },
        {
            icon: <FaUserMd className="text-orange-500" size={24} />,
            title: "Posture & Movement Analysis",
            description: "Observation of standing posture, walking gait, and movement patterns to identify postural issues or compensations."
        },
        {
            icon: <FaCheckCircle className="text-indigo-500" size={24} />,
            title: "Weakness Identification",
            description: "Comprehensive evaluation to pinpoint muscle imbalances, weak areas, and potential injury risks for targeted training."
        }
    ]

    const containerVariants = {
        hidden: { opacity: 0 },
        visible: {
            opacity: 1,
            transition: {
                staggerChildren: 0.2
            }
        }
    }

    const itemVariants = {
        hidden: { opacity: 0, y: 20 },
        visible: {
            opacity: 1,
            y: 0,
            transition: {
                duration: 0.5
            }
        }
    }

  return (
    <>
      <Head>
        <title>What to Expect from Your Personal Training Trial in Singapore</title>
        <meta charSet="utf-8"/>
        <meta httpEquiv="X-UA-Compatible" content="IE=edge"/>
        <meta name="viewport" content="width=device-width, initial-scale=1.0" />
        <meta name="description"
            content="Experience StringsSG's personal training trial: detailed consultation, comprehensive physical assessment, and expert guidance to select the perfect fitness package for your goals."/>
        <meta name="keywords"
            content="free personal training trial singapore, what to expect personal trainer, fitness assessment singapore, personal training consultation, stringsg trial"/>
        <meta name="author" content="StringsSG - Personal Training"/>
        <meta name="google-site-verification"
            content="JAjj5vAc8Xxrh4idRAchZhYR6rwxWPAk3vxMdx-dwVQ" />
        <meta property="og:locale" content="en_US" />
        <meta property="og:url"  content={`https://www.stringssg.com/sg/personal-trainer/what-to-expect-from-personal-training`} />
        <meta property="og:type"   content="article" />
        <meta property="og:title"    content="What to Expect from Your Personal Training Trial in Singapore"/>
        <meta property="og:description" content="Experience StringsSG's personal training trial: detailed consultation, comprehensive physical assessment, and expert guidance to select the perfect fitness package for your goals."/>
        <meta property="article:publisher" content="https://www.facebook.com/StringsSG/" />
        <meta property="article:published_time" content="2025-01-04T07:30:46+00:00" />
        <meta property="article:modified_time" content={modifiedDate} />
        <meta property="og:image"  content="https://stringssg-assets.s3-ap-southeast-1.amazonaws.com/static/personal-trainer/generated/serene-training-agatha.png" />
        <meta property="og:image:width" content="1200" />
        <meta property="og:image:height" content="630" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:site" content="@stringssg"/>
        <meta name="twitter:url" content={`https://www.stringssg.com/sg/personal-trainer/what-to-expect-from-personal-training`}/>
        <meta name="twitter:title" content="What to Expect from Your Personal Training Trial in Singapore"/>
        <meta name="twitter:description" content="Experience StringsSG's personal training trial: detailed consultation, comprehensive physical assessment, and expert guidance to select the perfect fitness package for your goals."/>
        <meta name="twitter:image" content="https://stringssg-assets.s3-ap-southeast-1.amazonaws.com/static/personal-trainer/generated/serene-training-agatha.png" />
        <link rel="canonical" href="https://www.stringssg.com/sg/personal-trainer/what-to-expect-from-personal-training" />
        <script type="application/ld+json"
            dangerouslySetInnerHTML={{ __html:
              `{"@context":"https://schema.org","@graph":[{"@type":"Service","@id":"https://www.stringssg.com/sg/personal-trainer/what-to-expect-from-personal-training/#service","url":"https://www.stringssg.com/sg/personal-trainer/what-to-expect-from-personal-training","name":"Personal Training Trial Singapore","description":"Experience StringsSG's personal training trial with detailed consultation, physical assessment, and package recommendations.","provider":{"@type":"Organization","name":"StringsSG","url":"https://www.stringssg.com","sameAs":["https://www.facebook.com/StringsSG/"]},"areaServed":"Singapore","serviceType":"Fitness Assessment","offers":{"@type":"Offer","price":"0","priceCurrency":"SGD","description":"Personal training trial session including consultation and assessment."}},{"@type":"WebPage","@id":"https://www.stringssg.com/sg/personal-trainer/what-to-expect-from-personal-training/#webpage","url":"https://www.stringssg.com/sg/personal-trainer/what-to-expect-from-personal-training/","name":"What to Expect from Your Personal Training Trial","description":"Learn what happens during StringsSG's personal training trial: consultation, assessment, and package selection guidance.","inLanguage":"en-US","isPartOf":{"@id":"https://www.stringssg.com/#website"},"breadcrumb":{"@id":"https://www.stringssg.com/sg/personal-trainer/what-to-expect-from-personal-training/#breadcrumblist"},"datePublished":"2025-01-04T07:30:46+00:00","dateModified":"${modifiedDate}","mainEntity":{"@type":"Service","name":"Personal Training Trial","description":"Trial session with consultation and physical assessment."}},{"@type":"BreadcrumbList","@id":"https://www.stringssg.com/sg/personal-trainer/what-to-expect-from-personal-training/#breadcrumblist","itemListElement":[{"@type":"ListItem","position":1,"item":{"@type":"WebPage","@id":"https://www.stringssg.com/","name":"Home","url":"https://www.stringssg.com/"}},{"@type":"ListItem","position":2,"item":{"@type":"WebPage","@id":"https://www.stringssg.com/sg/personal-trainer/","name":"Personal Trainer Singapore","url":"https://www.stringssg.com/sg/personal-trainer/"}},{"@type":"ListItem","position":3,"item":{"@type":"WebPage","@id":"https://www.stringssg.com/sg/personal-trainer/what-to-expect-from-personal-training/","name":"What to Expect from Personal Training Trial","url":"https://www.stringssg.com/sg/personal-trainer/what-to-expect-from-personal-training/"}}]},{"@type":"Organization","@id":"https://www.stringssg.com/#organization","name":"StringsSG","url":"https://www.stringssg.com/","foundingDate":"2018","description":"Singapore's leading platform for personal training services since 2018."},{"@type":"WebSite","@id":"https://www.stringssg.com/#website","url":"https://www.stringssg.com/","name":"StringsSG Singapore","inLanguage":"en-US","publisher":{"@id":"https://www.stringssg.com/#organization"},"potentialAction":{"@type":"SearchAction","target":"https://www.stringssg.com/search?q={search_term_string}","query-input":"required name=search_term_string"}}]}`
            }}
        />
      </Head>
        <div className="">
            <RequestProvider>
                {/* Visible trail back to the hub; the page already emits its own BreadcrumbList. */}
                <Breadcrumb jsonLd={false} pageTitle="What to Expect from Personal Training" />
                <PersonalTrainerHead
                    heroImage={s3Static + "personal-trainer/generated/serene-training-agatha.png"}
                    title="Personal Training Trial"
                    heading="Personal Training Trial"
                    heroCopy="What happens in your first session: assessment, goal-setting and a programme built around your starting point."
                    setIsQuickBook={setIsQuickBook}
                    openRequestForm={openRequestForm}
                    requestForm={requestForm}
                    setRequestForm={setRequestForm}
                    isQuickBook={isQuickBook}
                    isCustomBook={isCustomBook}
                    requestType={requestType}
                    requestCategorySettings={requestCategorySettings}
                />
            </RequestProvider>

            <header className="text-center py-8 px-2">
                <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">What to Expect from Your Trial Session</h2>
                <p className="text-gray-600 max-w-3xl mx-auto">Experience StringsSG&apos;s personal training trial with a comprehensive consultation, detailed physical assessment, and expert guidance to help you choose the perfect fitness package.</p>
                <p className="text-gray-600 max-w-3xl mx-auto mt-4">
                    Trials are priced differently across the market, and some studios charge for them while others do not. Our guide to{' '}
                    <Link href="/sg/personal-trainer/personal-trainer-rates-singapore" prefetch={false} className="text-orange-600 hover:text-orange-700 underline">
                        what trainers charge per session in Singapore
                    </Link>{' '}
                    covers trial pricing alongside full session rates.
                </p>
            </header>

            {/* Consultation Section */}
            <motion.section
                className="py-12 px-4 max-w-6xl mx-auto"
                initial="hidden"
                whileInView="visible"
                viewport={{ once: true }}
                variants={containerVariants}
            >
                <motion.div className="text-center mb-8" variants={itemVariants}>
                    <h2 className="text-2xl md:text-3xl font-bold text-gray-900 mb-4">📋 Detailed Consultation (15-20 minutes)</h2>
                    <p className="text-gray-600 max-w-2xl mx-auto">Your trainer takes time to understand your lifestyle, goals, and needs to create a personalized fitness plan.</p>
                </motion.div>

                <motion.div className="grid md:grid-cols-2 gap-6" variants={containerVariants}>
                    {consultationSteps.map((step, index) => (
                        <motion.div
                            key={index}
                            className="bg-white rounded-lg shadow-md p-6 border-l-4 border-orange-500"
                            variants={itemVariants}
                        >
                            <div className="flex items-start gap-4">
                                <div className="shrink-0">
                                    {step.icon}
                                </div>
                                <div>
                                    <h3 className="text-lg font-semibold text-gray-900 mb-2">{step.title}</h3>
                                    <p className="text-gray-600 text-sm leading-relaxed">{step.description}</p>
                                </div>
                            </div>
                        </motion.div>
                    ))}
                </motion.div>
            </motion.section>

            {/* Physical Assessment Section */}
            <motion.section
                className="py-12 px-4 bg-gray-50"
                initial="hidden"
                whileInView="visible"
                viewport={{ once: true }}
                variants={containerVariants}
            >
                <motion.div className="text-center mb-8 max-w-6xl mx-auto" variants={itemVariants}>
                    <h2 className="text-2xl md:text-3xl font-bold text-gray-900 mb-4">🏃‍♀️ Comprehensive Physical Assessment (20-30 minutes)</h2>
                    <p className="text-gray-600 max-w-2xl mx-auto">A thorough evaluation of your current fitness level, movement patterns, and physical capabilities to ensure safe and effective training.</p>
                </motion.div>

                <motion.div className="grid md:grid-cols-2 gap-6 max-w-6xl mx-auto" variants={containerVariants}>
                    {assessmentSteps.map((step, index) => (
                        <motion.div
                            key={index}
                            className="bg-white rounded-lg shadow-md p-6 border-l-4 border-blue-500"
                            variants={itemVariants}
                        >
                            <div className="flex items-start gap-4">
                                <div className="shrink-0">
                                    {step.icon}
                                </div>
                                <div>
                                    <h3 className="text-lg font-semibold text-gray-900 mb-2">{step.title}</h3>
                                    <p className="text-gray-600 text-sm leading-relaxed">{step.description}</p>
                                </div>
                            </div>
                        </motion.div>
                    ))}
                </motion.div>
            </motion.section>

            {/* Package Selection Section */}
            <motion.section
                className="py-12 px-4 max-w-6xl mx-auto"
                initial="hidden"
                whileInView="visible"
                viewport={{ once: true }}
                variants={containerVariants}
            >
                <motion.div className="text-center mb-8" variants={itemVariants}>
                    <h2 className="text-2xl md:text-3xl font-bold text-gray-900 mb-4">🎯 Personalized Package Recommendation</h2>
                    <p className="text-gray-600 max-w-2xl mx-auto">Based on your consultation and assessment results, your trainer helps you select the perfect package to achieve your fitness goals.</p>
                </motion.div>

                <motion.div className="bg-white rounded-lg shadow-lg p-8" variants={itemVariants}>
                    <div className="grid md:grid-cols-3 gap-6">
                        <div className="text-center">
                            <div className="bg-green-100 rounded-full w-16 h-16 flex items-center justify-center mx-auto mb-4">
                                <FaUserMd className="text-green-600" size={24} />
                            </div>
                            <h3 className="font-semibold text-gray-900 mb-2">Personalized Plan Creation</h3>
                            <p className="text-sm text-gray-600">Your assigned trainer creates a customized training and nutrition plan based on your goals and assessment results.</p>
                        </div>
                        <div className="text-center">
                            <div className="bg-blue-100 rounded-full w-16 h-16 flex items-center justify-center mx-auto mb-4">
                                <FaClipboardCheck className="text-blue-600" size={24} />
                            </div>
                            <h3 className="font-semibold text-gray-900 mb-2">Package Selection</h3>
                            <p className="text-sm text-gray-600">10-60 sessions based on your goals, timeline, and commitment level. Starting from $80/session.</p>
                        </div>
                        <div className="text-center">
                            <div className="bg-purple-100 rounded-full w-16 h-16 flex items-center justify-center mx-auto mb-4">
                                <FaCheckCircle className="text-purple-600" size={24} />
                            </div>
                            <h3 className="font-semibold text-gray-900 mb-2">Progress Monitoring</h3>
                            <p className="text-sm text-gray-600">Regular check-ins, progress photos, measurements, and performance tracking to ensure you&apos;re achieving your goals and adjust the program as needed.</p>
                        </div>
                    </div>
                </motion.div>
            </motion.section>

            <RequestProvider>
                <PersonalTrainerBooking
                    setIsQuickBook={setIsQuickBook}
                    openRequestForm={openRequestForm}
                    year={year}
                />
            </RequestProvider>
        </div>
        <Script
          id="google-tagmanager-new"
          src="https://www.googletagmanager.com/gtag/js?id=G-QD1HGGHDYJ"
          strategy="lazyOnload"
          onLoad={() => {
            window.dataLayer = window.dataLayer || [];
            function gtag(){dataLayer.push(arguments);}
            gtag('js', new Date());

            gtag('config', 'G-QD1HGGHDYJ');
          }}
        />
        <Script id="facebook-tracking" strategy="lazyOnload">
          {`!function(f,b,e,v,n,t,s)
                {if(f.fbq)return;n=f.fbq=function(){n.callMethod?
                n.callMethod.apply(n,arguments):n.queue.push(arguments)};
                if(!f._fbq)f._fbq=n;n.push=n;n.loaded=!0;n.version='2.0';
                n.queue=[];t=b.createElement(e);t.async=!0;
                t.src=v;s=b.getElementsByTagName(e)[0];
                s.parentNode.insertBefore(t,s)}(window, document,'script',
                'https://connect.facebook.net/en_US/fbevents.js');
                fbq('init', '128634041116395');
                fbq('track', 'PageView');`}
        </Script>
    </>
  )
}
